import { Box, Button, Chip, IconButton, MenuItem, Stack, TextField, Tooltip } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { useMemo, useState } from "react";
import { PagedTable, type Column, type SortState } from "../../components/PagedTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { UserFormDialog } from "./UserFormDialog";
import { ReviewUserDialog } from "./ReviewUserDialog";
import { UserProfileDialog } from "./UserProfileDialog";
import { downloadUserList, useDeleteUser, useRoles, useSetUserEnabled, useUsers } from "./api";
import { DEFAULT_USER_QUERY, type User, type UserQuery } from "../../types/user";
import { EmptyState } from "../../components/EmptyState";
import { roleLabel, userStatusColour, userStatusLabel } from "../../lib/labels";
import { usePageTitle } from "../../app/usePageTitle";
import { Authority } from "../../auth/authorities";
import { useAuth } from "../../auth/useAuth";
import { useDebounced } from "../../hooks/useDebounced";
import { useNotify } from "../../app/useNotify";
import { errorMessageFor } from "../../api/client";
import { formatDateTime } from "../../lib/format";

const STATUSES = [
  "REGISTRATION_PENDING",
  "APPROVED",
  "REGISTRATION_REJECTED",
  "REJECTED",
  "UNVARIFIED",
] as const;

export function UsersPage() {
  usePageTitle("Users");
  const { can } = useAuth();
  const notify = useNotify();
  const [query, setQuery] = useState<UserQuery>(DEFAULT_USER_QUERY);
  const [search, setSearch] = useState("");
  const [formUserId, setFormUserId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [viewing, setViewing] = useState<User | null>(null);
  const [reviewing, setReviewing] = useState<{ user: User; approved: boolean } | null>(null);
  const [deleting, setDeleting] = useState<User | null>(null);
  const [toggling, setToggling] = useState<User | null>(null);

  useDebounced(search, 400, (term) =>
    setQuery((previous) => ({ ...previous, search: term, pageNumber: 0 })),
  );

  const filtered =
    query.search !== "" ||
    query.status !== "" ||
    query.roles !== "" ||
    query.emailConfirmed !== DEFAULT_USER_QUERY.emailConfirmed;
  const users = useUsers(query);
  const roles = useRoles();
  const remove = useDeleteUser();
  const setEnabled = useSetUserEnabled();

  const columns = useMemo<Column<User>[]>(
    () => [
      { key: "email", label: "Email", sortable: true },
      { key: "firstName", label: "First name", sortable: true },
      { key: "lastName", label: "Last name", sortable: true },
      {
        key: "createdAt",
        label: "Created",
        sortable: true,
        render: (user) => formatDateTime(user.createdAt),
      },
      {
        key: "roles",
        label: "Roles",
        render: (user) => (
          <Stack direction="row" sx={{ gap: 0.5, flexWrap: "wrap" }}>
            {[...new Set(user.roles.map((role) => role.name))].map((name) => (
              <Chip key={name} label={roleLabel(name)} size="small" />
            ))}
          </Stack>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        render: (user) => (
          <Stack direction="row" sx={{ gap: 0.5, alignItems: "center" }}>
            <Chip
              label={userStatusLabel(user.status)}
              size="small"
              color={userStatusColour(user.status)}
            />
            {/* Self-explanatory rather than tooltipped: a tooltip replaces
                the chip's accessible name, so the label would be lost. */}
            {!user.emailConfirmed && (
              <Chip label="Email unconfirmed" size="small" variant="outlined" />
            )}
          </Stack>
        ),
      },
      {
        key: "actions",
        label: "Actions",
        align: "right",
        pinned: true,
        render: (user) => (
          <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
            {can(Authority.ApproveUser) && user.status !== "APPROVED" ? (
              <Tooltip title="Review registration">
                <IconButton
                  size="small"
                  aria-label={`Review ${user.email}`}
                  onClick={() => setViewing(user)}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              (can(Authority.ViewUsers) || can(Authority.EditUser)) && (
                <Tooltip title="View profile">
                  <IconButton
                    size="small"
                    aria-label={`View profile of ${user.email}`}
                    onClick={() => setViewing(user)}
                  >
                    <PersonSearchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            )}
            {can(Authority.EditUser) && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  aria-label={`Edit ${user.email}`}
                  onClick={() => {
                    setFormUserId(user.id);
                    setFormOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {can(Authority.DisableUser) && (
              <Tooltip title={user.enabled ? "Disable" : "Enable"}>
                <IconButton
                  size="small"
                  aria-label={`${user.enabled ? "Disable" : "Enable"} ${user.email}`}
                  onClick={() => setToggling(user)}
                >
                  <BlockIcon fontSize="small" color={user.enabled ? "inherit" : "disabled"} />
                </IconButton>
              </Tooltip>
            )}
            {can(Authority.DeleteUser) && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  aria-label={`Delete ${user.email}`}
                  onClick={() => setDeleting(user)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    [can],
  );

  async function download() {
    try {
      const blob = await downloadUserList();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "User.xlsx";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      notify.error(errorMessageFor(error) ?? "Could not download the user list.");
    }
  }

  return (
    <Box>
      <Stack
        direction="row"
        sx={{ gap: 2, justifyContent: "space-between", alignItems: "center", mb: 2 }}
      >
        <Stack direction="row" sx={{ gap: 1 }}>
          {can(Authority.ViewUsers) && (
            <Button startIcon={<DownloadIcon />} onClick={download}>
              Download
            </Button>
          )}
          {can(Authority.CreateUser) && (
            <Button
              variant="contained"
              onClick={() => {
                setFormUserId(null);
                setFormOpen(true);
              }}
            >
              New user
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap", mb: 2 }}>
        <TextField
          label="Search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          label="Status"
          value={query.status}
          onChange={(event) =>
            setQuery((previous) => ({ ...previous, status: event.target.value, pageNumber: 0 }))
          }
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {userStatusLabel(status)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Role"
          value={query.roles}
          onChange={(event) =>
            setQuery((previous) => ({ ...previous, roles: event.target.value, pageNumber: 0 }))
          }
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {(roles.data ?? []).map((role) => (
            <MenuItem key={role.id} value={String(role.id)}>
              {roleLabel(role.name)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Email confirmed"
          value={query.emailConfirmed}
          onChange={(event) =>
            setQuery((previous) => ({
              ...previous,
              emailConfirmed: event.target.value as UserQuery["emailConfirmed"],
              pageNumber: 0,
            }))
          }
          sx={{ minWidth: 200 }}
          helperText={
            query.emailConfirmed === "true" ? "Unconfirmed signups are hidden" : undefined
          }
        >
          <MenuItem value="true">Confirmed only</MenuItem>
          <MenuItem value="false">Unconfirmed only</MenuItem>
          <MenuItem value="any">Any</MenuItem>
        </TextField>
        <Button
          onClick={() => {
            setSearch("");
            setQuery(DEFAULT_USER_QUERY);
          }}
        >
          Reset
        </Button>
      </Stack>

      <PagedTable
        columns={columns}
        rows={users.data?.data ?? []}
        rowKey={(user) => user.id}
        total={users.data?.total ?? 0}
        page={query.pageNumber}
        pageSize={query.pageSize}
        sort={{ sortOn: query.sortOn, sort: query.sort }}
        loading={users.isFetching}
        empty={
          <EmptyState
            filtered={filtered}
            noun="users"
            onClearFilters={() => {
              setSearch("");
              setQuery(DEFAULT_USER_QUERY);
            }}
          />
        }
        onPageChange={(page) => setQuery((previous) => ({ ...previous, pageNumber: page }))}
        onPageSizeChange={(pageSize) =>
          setQuery((previous) => ({ ...previous, pageSize, pageNumber: 0 }))
        }
        onSortChange={(next: SortState) =>
          setQuery((previous) => ({ ...previous, ...next, pageNumber: 0 }))
        }
      />

      <UserFormDialog open={formOpen} userId={formUserId} onClose={() => setFormOpen(false)} />

      <UserProfileDialog
        user={viewing}
        canReview={can(Authority.ApproveUser)}
        onReview={(user, approved) => setReviewing({ user, approved })}
        onClose={() => setViewing(null)}
      />

      <ReviewUserDialog
        key={reviewing?.user.id ?? "none"}
        user={reviewing?.user ?? null}
        approved={reviewing?.approved ?? true}
        onClose={() => setReviewing(null)}
        onReviewed={() => setViewing(null)}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={`Delete ${deleting?.email}?`}
        message="Their account and history are removed. This cannot be undone."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            notify.success("User deleted");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Unable to delete the user.");
          } finally {
            setDeleting(null);
          }
        }}
      />

      <ConfirmDialog
        open={toggling !== null}
        title={`${toggling?.enabled ? "Disable" : "Enable"} ${toggling?.email}?`}
        message={
          toggling?.enabled
            ? "They will not be able to sign in until you enable the account again."
            : "They will be able to sign in again."
        }
        confirmLabel={toggling?.enabled ? "Disable" : "Enable"}
        busy={setEnabled.isPending}
        onClose={() => setToggling(null)}
        onConfirm={async () => {
          if (!toggling) return;
          try {
            await setEnabled.mutateAsync({ userId: toggling.id, enabled: !toggling.enabled });
            notify.success(toggling.enabled ? "Account disabled" : "Account enabled");
          } catch (error) {
            notify.error(errorMessageFor(error) ?? "Could not change the account status.");
          } finally {
            setToggling(null);
          }
        }}
      />
    </Box>
  );
}
